
import { useState, useRef, useEffect } from "react";
import { useChat } from "@/hooks/useChat";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";
import { useToast } from "@/hooks/use-toast";
import { Message, ChatMessage } from "@/types/chat";
import { ChatLoadingState } from "./ChatLoadingState";
import { useChatLimit } from "@/hooks/useChatLimit";
import { ChatLimitPopup } from "./ChatLimitPopup";

interface ChatInterfaceProps {
  videoId: string;
}

export const ChatInterface = ({ videoId }: ChatInterfaceProps) => {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const {
    messages,
    isLoading,
    error,
    sendMessage
  } = useChat(videoId);

  const { 
    remainingMessages, 
    hasSubscription,
    showLimitPopup, 
    setShowLimitPopup,
    decrementRemainingMessages 
  } = useChatLimit(videoId);

  // Convert ChatMessage[] to Message[] for compatibility with existing components
  const convertedMessages: Message[] = messages.map(chatMsg => ({
    id: parseInt(chatMsg.id),
    text: chatMsg.content,
    isUser: chatMsg.role === 'user',
    isTyping: false
  }));

  // Display error toast if there's an error from useChat
  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        variant: "destructive",
      });
    }
  }, [error, toast]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Check if user has remaining messages or a subscription
    if (remainingMessages <= 0 && !hasSubscription) {
      setShowLimitPopup(true);
      return;
    }

    try {
      // Decrement remaining messages counter
      if (!hasSubscription) {
        decrementRemainingMessages();
      }
      
      await sendMessage(input);
      setInput("");
    } catch (err) {
      console.error('Error submitting message:', err);
      toast({
        title: "Error",
        description: err instanceof Error ? err.message : "Failed to send message",
        variant: "destructive",
      });
    }
  };

  if (isLoading && !messages.length) {
    return <ChatLoadingState message="Loading chat history..." />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-16rem)] md:h-[500px] bg-muted/95 backdrop-blur-md rounded-lg relative border border-gray-800/50">
      <ChatMessages
        messages={convertedMessages}
        isLoading={isLoading}
        messagesEndRef={messagesEndRef}
      />
      <ChatInput
        input={input}
        isLoading={isLoading}
        onInputChange={setInput}
        onSubmit={handleSubmit}
        remainingMessages={remainingMessages}
        hasSubscription={hasSubscription}
      />
      
      {/* Chat Limit Popup */}
      <ChatLimitPopup 
        isOpen={showLimitPopup} 
        onOpenChange={setShowLimitPopup} 
      />
    </div>
  );
};
